import { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import {
  IUseHookAxiosError,
  IUseHookIsError,
  IUseHookIsFinished,
  IUseHookIsSuccess,
  IUseHookResponse,
  IUseHookErrorData,
} from 'hooks/@types';
import ChatService from 'services/Chat/Chat.service';
import { getFbUUID, getFbclid, getFbp, isFBFlow } from 'helpers';
import { IServiceChatValidateUserProps } from 'services/Chat/@types';
import IUseUserValidation, { IUseUserValidationData, IUseUserValidationProps } from './@types';

function useUserValidation(): IUseUserValidation {
  const abortController = new AbortController();

  const [answering, setAnswering] = useState<boolean>(false);
  const [data, setData] = useState<IUseUserValidationData | null>(null);
  const [isFinished, setIsFinished] = useState<IUseHookIsFinished>(false);

  const [isError, setIsError] = useState<IUseHookIsError>(null);
  const [isSuccess, setIsSuccess] = useState<IUseHookIsSuccess>(null);

  const [error, setError] = useState<IUseHookErrorData | null>(null);
  const [response, setResponse] = useState<IUseHookResponse>(null);
  const [axiosErrorResponse, setAxiosErrorResponse] = useState<IUseHookAxiosError>(null);

  const validate = async (values: IUseUserValidationProps): Promise<AxiosResponse> => {
    setResponse(null);
    setData(null);
    setAnswering(true);
    setIsFinished(false);
    setError(null);
    setIsSuccess(undefined);
    setIsError(undefined);
    setAxiosErrorResponse(null);

    const valuesBody: IServiceChatValidateUserProps = values;

    if (isFBFlow()) {
      valuesBody.fbUUID = getFbUUID();
      valuesBody.fbc = getFbclid();
      valuesBody.fbp = getFbp();
    }

    return ChatService.validateUser({
      ...valuesBody,
      signal: values?.signal || abortController.signal,
    })
      .then((responseRequest) => {
        setIsSuccess(true);
        setIsError(false);
        setResponse(responseRequest);
        setData(responseRequest?.data?.data);
        return responseRequest;
      })
      .catch((axiosError) => {
        setAxiosErrorResponse(axiosError);
        setIsSuccess(false);
        setIsError(true);
        setError(axiosError?.response?.data?.error_data || axiosError?.response?.data || axiosError?.response);
        return axiosError?.response;
      })
      .finally(() => {
        setAnswering(false);
        setIsFinished(true);
      });
  };

  useEffect(
    () => () => {
      abortController.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    response,
    axiosErrorResponse,
    isSuccess,
    isFinished,
    isError,

    answering,
    data,
    validate,
    error,
  };
}

export default useUserValidation;
export type { IUseUserValidationData };
